import {
  FunctionComponent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import api from '../../Redux/Axios/middleware';

interface Point {
  x: number;
  y: number;
}

interface LevelCalibration {
  meters_per_pixel?: number | null;
  origin_px?: { x: number | null; y: number | null } | null;
  rotation_deg?: number | null;
  image_width_px?: number | null;
  image_height_px?: number | null;
  calibrated_at?: string | null;
  calibrated_by?: string | null;
}

interface LevelCalibrateModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  level: {
    _id: string;
    level_id: string;
    level_diagram?: string;
    diagram_calibration?: LevelCalibration;
  } | null;
}

const MAX_WIDTH = 720;

const LevelCalibrateModal: FunctionComponent<LevelCalibrateModalProps> = ({
  open,
  onClose,
  onSaved,
  level,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [displayed, setDisplayed] = useState<{ w: number; h: number } | null>(
    null
  );
  const [pointA, setPointA] = useState<Point | null>(null);
  const [pointB, setPointB] = useState<Point | null>(null);
  const [distanceM, setDistanceM] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  const diagramSrc = useMemo(() => {
    if (!level?.level_diagram) return null;
    return `${process.env.REACT_APP_API_URL}/${level.level_diagram}`;
  }, [level?.level_diagram]);

  useEffect(() => {
    if (!open) return;
    const savedOrigin = level?.diagram_calibration?.origin_px;
    setPointA(
      savedOrigin?.x != null && savedOrigin?.y != null
        ? { x: savedOrigin.x as number, y: savedOrigin.y as number }
        : null
    );
    setPointB(null);
    setDistanceM('');
    setError(null);
    setLoadError(false);
    setNatural(null);
    setDisplayed(null);
  }, [open, level?._id]);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (!img || !natural) return;
    const ro = new ResizeObserver(() => {
      setDisplayed({ w: img.clientWidth, h: img.clientHeight });
    });
    ro.observe(img);
    return () => ro.disconnect();
  }, [natural, open]);

  const handleLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    setDisplayed({ w: img.clientWidth, h: img.clientHeight });
    setLoadError(false);
  };

  const handleImgError = () => {
    setLoadError(true);
    setNatural(null);
    setDisplayed(null);
  };

  const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!natural || submitting) return;
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const naturalX = Math.round((clickX / rect.width) * natural.w);
    const naturalY = Math.round((clickY / rect.height) * natural.h);
    const clamp = (v: number, max: number) => Math.max(0, Math.min(max - 1, v));
    const pt = { x: clamp(naturalX, natural.w), y: clamp(naturalY, natural.h) };
    if (!pointA) {
      setPointA(pt);
    } else if (!pointB) {
      setPointB(pt);
    } else {
      // Third+ click starts over.
      setPointA(pt);
      setPointB(null);
    }
  };

  const pixelDistance = useMemo(() => {
    if (!pointA || !pointB) return null;
    const dx = pointB.x - pointA.x;
    const dy = pointB.y - pointA.y;
    return Math.hypot(dx, dy);
  }, [pointA, pointB]);

  const computed = useMemo(() => {
    if (!pointA || !pointB || !pixelDistance || pixelDistance < 1) return null;
    const dist = Number(distanceM);
    if (!Number.isFinite(dist) || dist <= 0) return null;
    const mpp = dist / pixelDistance;
    const dx = pointB.x - pointA.x;
    const dy = pointB.y - pointA.y;
    const rotation_deg = (Math.atan2(dy, dx) * 180) / Math.PI;
    return {
      meters_per_pixel: mpp,
      origin_px: { x: pointA.x, y: pointA.y },
      rotation_deg,
    };
  }, [pointA, pointB, pixelDistance, distanceM]);

  const toDisplay = (pt: Point | null): { left: number; top: number } | null => {
    if (!pt || !natural || !displayed) return null;
    return {
      left: (pt.x / natural.w) * displayed.w,
      top: (pt.y / natural.h) * displayed.h,
    };
  };
  const aDisp = toDisplay(pointA);
  const bDisp = toDisplay(pointB);

  const canSave = Boolean(level?._id) && Boolean(computed) && !submitting;

  const handleSave = async () => {
    if (!level?._id || !computed || !natural) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.put(`/levels/${level._id}/calibration`, {
        meters_per_pixel: computed.meters_per_pixel,
        origin_px: computed.origin_px,
        rotation_deg: computed.rotation_deg,
        image_width_px: natural.w,
        image_height_px: natural.h,
      });
      onSaved?.();
      onClose();
    } catch (err: any) {
      const serverMsg =
        err?.response?.data?.response ??
        err?.response?.data?.responseMessage ??
        err?.message ??
        'Request failed';
      setError(
        typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const existing = level?.diagram_calibration;
  const existingSummary =
    existing &&
    existing.meters_per_pixel != null &&
    Number.isFinite(Number(existing.meters_per_pixel))
      ? `Currently calibrated: ${Number(existing.meters_per_pixel).toFixed(5)} m/px` +
        (existing.calibrated_at
          ? ` (${new Date(existing.calibrated_at).toLocaleString()})`
          : '')
      : 'This level is not calibrated yet.';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Calibrate level {level?.level_id ? `“${level.level_id}”` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 8 }}>
          Click two points on the diagram whose real-world distance you know,
          then enter that distance in meters. The first click becomes the
          metric origin.
        </div>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: existing?.meters_per_pixel != null ? '#166534' : '#92400e',
          background: existing?.meters_per_pixel != null ? '#dcfce7' : '#fef3c7',
          border: `1px solid ${existing?.meters_per_pixel != null ? '#86efac' : '#fcd34d'}`,
          borderRadius: 6,
          padding: '6px 12px',
          marginBottom: 12,
          display: 'inline-block',
        }}>
          {existingSummary}
        </div>

        {!diagramSrc ? (
          <Alert severity="warning">
            This level has no diagram image. Upload a level_diagram first.
          </Alert>
        ) : loadError ? (
          <Alert severity="error">Failed to load the diagram image.</Alert>
        ) : (
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
              maxWidth: MAX_WIDTH,
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              overflow: 'hidden',
              background: '#f3f4f6',
              cursor: submitting ? 'not-allowed' : 'crosshair',
            }}>
            <img
              ref={imgRef}
              src={diagramSrc}
              alt="level diagram"
              onLoad={handleLoad}
              onError={handleImgError}
              onClick={handleClick}
              draggable={false}
              style={{
                display: 'block',
                width: '100%',
                maxWidth: MAX_WIDTH,
                height: 'auto',
                userSelect: 'none',
              }}
            />
            {aDisp ? (
              <Marker left={aDisp.left} top={aDisp.top} label="A" color="#2563eb" />
            ) : null}
            {bDisp ? (
              <Marker left={bDisp.left} top={bDisp.top} label="B" color="#dc2626" />
            ) : null}
            {aDisp && bDisp && displayed ? (
              <svg
                width={displayed.w}
                height={displayed.h}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  pointerEvents: 'none',
                }}>
                <line
                  x1={aDisp.left}
                  y1={aDisp.top}
                  x2={bDisp.left}
                  y2={bDisp.top}
                  stroke="#111827"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                />
              </svg>
            ) : null}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 14,
          }}>
          <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
            <div>
              <b>A (origin):</b>{' '}
              {pointA ? `(${pointA.x}, ${pointA.y}) px` : '— click to set'}
            </div>
            <div>
              <b>B:</b>{' '}
              {pointB ? `(${pointB.x}, ${pointB.y}) px` : '— click to set'}
            </div>
            <div>
              <b>Pixel distance:</b>{' '}
              {pixelDistance != null ? `${pixelDistance.toFixed(2)} px` : '—'}
            </div>
          </div>

          <TextField
            label="Real-world distance (meters)"
            type="number"
            size="small"
            value={distanceM}
            onChange={(e) => setDistanceM(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            disabled={!pointA || !pointB || submitting}
            style={{ maxWidth: 320 }}
          />

          {computed ? (
            <div
              style={{
                fontSize: 12,
                color: '#374151',
                background: '#f9fafb',
                padding: 10,
                borderRadius: 4,
                border: '1px solid #e5e7eb',
              }}>
              <div>
                meters_per_pixel ={' '}
                <b>{computed.meters_per_pixel.toFixed(6)}</b>
              </div>
              <div>
                origin_px ={' '}
                <b>
                  ({computed.origin_px.x}, {computed.origin_px.y})
                </b>
              </div>
              <div>
                rotation_deg = <b>{computed.rotation_deg.toFixed(2)}°</b>
              </div>
            </div>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setPointA(null);
            setPointB(null);
            setDistanceM('');
            setError(null);
          }}
          disabled={submitting || (!pointA && !pointB)}>
          Reset points
        </Button>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}>
          {submitting ? 'Saving…' : 'Save calibration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Marker: FunctionComponent<{
  left: number;
  top: number;
  label: string;
  color: string;
}> = ({ left, top, label, color }) => (
  <>
    <div
      style={{
        position: 'absolute',
        left: left - 9,
        top: top - 9,
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: color,
        border: '2px solid #fff',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
        pointerEvents: 'none',
      }}
    />
    <div
      style={{
        position: 'absolute',
        left: left + 10,
        top: top - 20,
        padding: '0 4px',
        fontSize: 11,
        fontWeight: 700,
        color: '#fff',
        background: color,
        borderRadius: 2,
        pointerEvents: 'none',
      }}>
      {label}
    </div>
  </>
);

export default LevelCalibrateModal;
